<script lang="ts">
import { defineComponent, PropType, watch, onMounted } from 'vue';
import useUnitLogic from '../../../composables/useUnitLogic';
import { useI18n } from 'vue-i18n';
import { loadHeroDetails } from '@api/roster';
import { useLocalizedNamesStore } from '../../../stores/localizedNames';
import type { UnitType } from '@interfaces/unit_type';
import type { StructureType } from '@interfaces/structure_type';
import type { RosterType } from '@interfaces/roster_type';

export default defineComponent({
  name: 'SelectUnit',
  props: {
    roster: {
      type: Object as PropType<RosterType>,
      required: true
    },
    structure: {
      type: Object as PropType<StructureType>,
      required: true
    },
    available_units: {
      type: Array as PropType<UnitType[]>,
      default: null
    },
    multiplier: {
      type: Number,
      default: 1
    },
    child: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      heroDetailsLoading: false,
    };
  },
  watch: {
    'structure.hero': {
      handler() {
        this.loadHeroDetailsIfNeeded();
      },
      deep: false,
      immediate: true
    },
  },
  mounted() {
    this.loadHeroDetailsIfNeeded();
  },
  methods: {
    findUnitInTree(units: UnitType[], techName: string | null): UnitType | null {
      if (!techName) return null;
      for (const u of units) {
        if (u.tech_name === techName) return u;
        if (Array.isArray(u.available_units) && u.available_units.length > 0) {
          const found = this.findUnitInTree(u.available_units, techName);
          if (found) return found;
        }
      }
      return null;
    },
    async loadHeroDetailsIfNeeded(): Promise<void> {
      const hero = this.structure.hero;
      if (!hero) return;
      const hasDetails = (hero.available_units?.length ?? 0) > 0 || (hero.available_spells?.length ?? 0) > 0 || (hero.available_equipment?.length ?? 0) > 0;
      const missingAlternateCardFields =
        hero.card_image_path_2 === undefined || hero.card_back_image_path_2 === undefined;
      if (hasDetails && !missingAlternateCardFields) return;

      this.heroDetailsLoading = true;
      try {
        const details = await loadHeroDetails(hero.id ?? null, hero.tech_name ?? undefined);
        if (details) {
          const availableUnits = details.available_units ?? [];
          this.structure.hero = {
            ...hero,
            available_units: availableUnits,
            available_spells: details.available_spells ?? [],
            available_equipment: details.available_equipment ?? [],
            image_path: details.image_path ?? hero.image_path,
            card_image_path: details.card_image_path ?? hero.card_image_path,
            card_back_image_path: details.card_back_image_path ?? hero.card_back_image_path,
            card_image_path_2: details.card_image_path_2 ?? hero.card_image_path_2,
            card_back_image_path_2: details.card_back_image_path_2 ?? hero.card_back_image_path_2,
          };
          // Enrich selected units with available_units so parent-child logic works when editing
          this.structure.units = (this.structure.units ?? []).map((sel) => {
            const full = this.findUnitInTree(availableUnits, sel.tech_name ?? null);
            if (full?.available_units) {
              return { ...sel, available_units: full.available_units };
            }
            return sel;
          });
        }
      } finally {
        this.heroDetailsLoading = false;
      }
    },
  },
  setup(props) {
    const i18n = useI18n();
    const namesStore = useLocalizedNamesStore();

    const {
      selected_units,
      selected_uniq_keys,
      is_subunit,
      get_parent_unit,
      max_quantity,
      available_quantity,
      add_unit,
      subtract_unit
    } = useUnitLogic(props.structure);

    const flattenUnits = (units: UnitType[]): UnitType[] => {
      const result: UnitType[] = [];
      units.forEach((u) => {
        result.push(u);
        if (Array.isArray(u.available_units) && u.available_units.length > 0) {
          result.push(...flattenUnits(u.available_units));
        }
      });
      return result;
    };

    const loadUnitNames = async () => {
      const baseUnits =
        (props.available_units && props.available_units.length > 0)
          ? props.available_units
          : (props.structure.hero?.available_units || []);

      if (!baseUnits?.length) return;

      const allUnits = flattenUnits(baseUnits);
      const items = allUnits
        .filter((u) => u.id !== null)
        .map((u) => ({ type: 'unit' as const, id: u.id as number }));

      await namesStore.ensureNamesLoaded(items, i18n.locale.value);
    };

    onMounted(() => {
      loadUnitNames();
    });

    watch(() => i18n.locale.value, () => {
      loadUnitNames();
    });

    watch(() => props.structure.hero?.id, () => {
      loadUnitNames();
    });

    watch(() => props.structure.hero?.available_units?.length, () => {
      loadUnitNames();
    });

    watch(() => props.available_units, () => {
      loadUnitNames();
    });

    const handleAddUnit = (unit: UnitType): void => {
      add_unit(unit);
    };

    const handleSubtractUnit = (unit: UnitType): void => {
      subtract_unit(unit);
    };

    return {
      selected_units,
      selected_uniq_keys,
      is_subunit,
      get_parent_unit,
      max_quantity,
      available_quantity,
      handleAddUnit,
      handleSubtractUnit,
      t: i18n.t,
      locale: i18n.locale,
      namesStore,
    };
  },
  computed: {
    units() {
      return this.available_units
        ? this.available_units.sort((a, b) => a.weight - b.weight)
        : this.structure.hero?.available_units?.sort((a, b) => a.weight - b.weight) || []
    },
    sources() {
      return (unit: UnitType) => ({
        'hero': this.structure.hero,
        'parent': this.get_parent_unit(unit)
      });
    },
    selectedQuantity() {
      return (unit: UnitType) => ({
        self: this.selected_units.find((item: UnitType) => item.tech_name === unit.tech_name)?.quantity || 0,
        parent: this.is_subunit(unit)
          ? (this.selected_units.find((item: UnitType) => item.tech_name === this.get_parent_unit(unit)?.tech_name)?.quantity || 0)
          : 0,
        uniqKeys: this.selected_uniq_keys
      });
    },
    availableQuantity() {
      return (unit: UnitType) => this.available_quantity(unit);
    }
  }
});
</script>

<template>
  <div id="unit_gallery">
    <div v-if="heroDetailsLoading" class="text-center">{{ t('select_domain.loading') }}...</div>
    <template v-else v-for="unit in units" :key="unit.tech_name">
      <unit :unit="unit"
            :structure="structure"
            :selected="selectedQuantity(unit)"
            :sources="sources(unit)"
            @addUnit="handleAddUnit(unit)"
            @subtractUnit="handleSubtractUnit(unit)"
            @open-unit-modal="$emit('open-unit-modal', $event)"
            @click.stop.prevent
            v-if="!is_subunit(unit)">
      </unit>

      <template v-if="unit.available_units?.length > 0">
        <unit v-for="u in unit.available_units"
              :key="u.tech_name"
              :unit="u"
              :structure="structure"
              :selected="selectedQuantity(u)"
              :sources="sources(u)"
              @addUnit="handleAddUnit(u)"
              @subtractUnit="handleSubtractUnit(u)"
              @open-unit-modal="$emit('open-unit-modal', $event)">
        </unit>
      </template>
      <!-- <p class="max_quantity" v-if="!is_subunit(unit)">
        {{ $t('units.available') }}&nbsp;<span>{{ availableQuantity(unit) }}</span>
      </p> -->
    </template>
  </div>
</template>
